<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		:transparent-header="true"
		:is-venture="true"
		:hide-header="true"
	>
		<BasePlaceHolder
			:section-count="3"
			:line-count="3"
			:placehold="loading"
			theme="dark"
			:class="[{ 'marketing-section': loading }, assetHeroConfig.backgroundColor]"
		>
			<AssetPageHero
				:hero="assetHeroConfig"
				:form-config="leadCaptureFormConfig"
				:offering-id="growthTechReitId"
				:logo="logoUrl"
			>
				<p
					class="body-sm body-xs-smo text-align-center-smo text-color-white"
					v-html="deorphanText(leadCTASubText)"
				/>
			</AssetPageHero>
		</BasePlaceHolder>
		<LogosList
			v-if="ventureLogos.length > 0"
			class="venture-logos-list"
			:heading="ventureLogosHeading"
			:logos="ventureLogos"
			:background-color="ventureLogosBackground"
			:opaque="true"
			marquee-duration="45s"
			marquee-gap="3rem"
			marquee-logos-size="1.75rem"
		/>
		<VentureCapitalCaseStudy v-if="caseStudyData" :case-study="caseStudyData" />
		<GrowthTechCompanies v-if="hasAssets" :asset-elements="assetElements" />
		<VentureCapitalAboutTheFund />
		<BasePlaceHolder :section-count="4" :placehold="loading" class="container marketing-section py-400-md">
			<h2 class="heading-5 mb-50-smo mb-150-md">Literature</h2>
			<LiteratureSection
				v-if="showLiterature"
				:literature-list="literatureItems"
				:disclaimer="literatureDisclaimer"
				disclaimer-classes="body-lg-md mt-150"
			/>
		</BasePlaceHolder>
		<template #footer>
			<BasePlaceHolder :line-count="3" :placehold="loading" :class="{ container: loading }">
				<FooterFull :disclaimers="footerDisclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>
<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { SanityImage, SanityRichTextBlock } from 'types/sanity';
import { SanityMarketingCaseStudy, SanityMarketingLogosList } from 'types/sanity/marketing-components';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import { deorphanText } from '@filters/shared-filters';
import FooterFull from '@components/footer/footer-full.vue';
import { FundLiterature } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import { getMarketingPageVentureAssets } from '@api/sanity/proxy/assets';
import { getVentureMarketingPage } from '@api/sanity/graphql/venture-marketing-page';
import GrowthTechCompanies from '@components/offering-details/growth-tech-companies.vue';
import { growthTechReitId } from '@constants/reit';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import LogosList from '@components/marketing-site/logos-list.vue';
import { MarketingImageHero } from 'types/layout';
import { useHead } from '@unhead/vue';
import VentureCapitalAboutTheFund from '@components/marketing-site/venture-capital/venture-capital-about-the-fund.vue';
import VentureCapitalCaseStudy from '@components/marketing-site/venture-capital/venture-case-study.vue';
import { ventureCapitalExpensedFunnelPlanId } from '@constants/plans-info';

const literatureItems = ref<Array<FundLiterature>>([]);
const literatureDisclaimer = ref<Array<SanityRichTextBlock>>([]);
const assetHeroConfig: MarketingImageHero = {
	backgroundColor: 'bg-color-content-primary',
	description:
		'Now you can invest early in what we believe to be the most innovative tech companies, including major players in the AI revolution—<i>before</i> they go&nbsp;public.',
	heading: 'Fundrise Innovation Fund',
	title: 'Invest in tomorrow’s great tech companies,&nbsp;today',
	imageUrl: `${appCloudfrontPath}/images/marketing-site/hero-images/hero-img-venture-no-labels.png`
};

const leadCTASubText = 'Start investing in less than 5 minutes and with as little as $10.';
const logoUrl = `${appCloudfrontPath}/images/global/logo-horiz-reverse-innovation.svg`;

const assetElements: Ref<Array<Asset>> = ref([]);
const loading = ref(true);

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	buttonText: 'Get started',
	presetPlanId: ventureCapitalExpensedFunnelPlanId,
	signupFlowType: 'FUND_MARKETING',
	thirdPartyAuthTheme: 'SECONDARY'
};

const caseStudyData: Ref<SanityMarketingCaseStudy | null> = ref(null);

const logosData: Ref<SanityMarketingLogosList | undefined> = ref(undefined);

const footerDisclaimers: Ref<Array<SanityRichTextBlock>> = ref([]);

const ventureLogos = computed(() => logosData.value?.logos.map((logo: SanityImage) => logo.asset.url) ?? []);

const ventureLogosHeading = computed((): string => logosData.value?.logosHeading ?? '');

const ventureLogosBackground = computed(
	(): string | undefined => logosData.value?.logosBackgroundColor?.hex ?? undefined
);

const hasAssets = computed((): boolean => assetElements.value.length > 0);

const showLiterature = computed((): boolean => literatureItems.value?.length > 0);

async function setup(): Promise<void> {
	useHead({
		title: 'The Fundrise Innovation Fund',
		meta: [
			{ property: 'og:title', content: 'Venture Capital' },
			{
				name: 'description',
				content: 'Invest in tomorrow’s great tech companies, today'
			},
			{
				property: 'og:description',
				content: 'Invest in tomorrow’s great tech companies, today'
			}
		]
	});
	try {
		const [assetsData, fundDetails, sanityResponseData] = await Promise.all([
			getMarketingPageVentureAssets(),
			getFund(growthTechReitId),
			getVentureMarketingPage('innovation-fund-view')
		]);
		assetElements.value = assetsData;
		literatureItems.value = fundDetails?.literature ?? [];
		literatureDisclaimer.value = fundDetails?.literatureDisclaimer ?? [];
		caseStudyData.value = sanityResponseData?.caseStudyPanel ?? null;
		logosData.value = sanityResponseData?.ventureLogos;
		footerDisclaimers.value = sanityResponseData?.flatFooterDisclaimers ?? [];
	} catch {
	} finally {
		loading.value = false;
	}
}

setup();
</script>
